import React, { useMemo, useState } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import isoWeek from 'dayjs/plugin/isoWeek';

import CalendarHeader from './CalendarHeader';

import {
  getPriceColors,
  lightenColor
} from './prices';

// Extend dayjs with necessary plugins
dayjs.extend(weekday);
dayjs.extend(isoWeek);

const getContrastColor = (color, brand) => {
  if (!color || typeof color !== 'string') {
    return '#ffffff';
  }

  const hex = color.replace('#', '');
  if (hex.length !== 6) {
    return '#ffffff';
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 170 ? (brand.black || '#0e1513') : '#ffffff';
};

const Calendar = ({ holidays = [], onConfirm, confirmLabel = 'Confirm Selection' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const priceColors = useMemo(() => getPriceColors(theme), [theme]);
  const priorities = useMemo(() => [
    priceColors.low,
    priceColors.medium,
    priceColors.high,
    priceColors.veryHigh
  ], [priceColors]);
  const brand = theme.palette.brand || {};

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [daysCount, setDaysCount] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const today = dayjs();
  const holidaySet = useMemo(() => new Set(holidays.map(h => h.date)), [holidays]);

  const isSelected = date => {
    if (!startDate && !endDate) return false;
    let d = dayjs(date);
    let sd = startDate ? dayjs(startDate) : null;
    let ed = endDate ? dayjs(endDate) : null;

    return (sd && d.isSame(sd, 'day')) || (ed && d.isSame(ed, 'day'));
  }

  const isInRange = date => {
    if (!startDate || !endDate) return false;
    let d = dayjs(date);
    let sd = dayjs(startDate);
    let ed = dayjs(endDate);

    return d.isAfter(sd, 'day') && d.isBefore(ed, 'day');
  };

  const getSelectedPriceColor = () => {
    if (!startDate || !endDate) {
      return priceColors.low;
    }

    let cd = dayjs(startDate);
    const ed = dayjs(endDate);
    let highestPrice = getDatePrice(cd.toDate());
    let highestPriority = priorities.indexOf(highestPrice);

    while (cd.isBefore(ed, 'day') || cd.isSame(ed, 'day')) {
      const price = getDatePrice(cd.toDate());
      const priority = priorities.indexOf(price);
      if (priority > highestPriority) {
        highestPriority = priority;
        highestPrice = price;
      }
      cd = cd.add(1, 'day');
    }

    return highestPrice;
  }

  const handleDayClick = (date) => {
    let d = dayjs(date);
    let sd = dayjs(startDate);
    let ed = dayjs(endDate);

    if (!startDate || (startDate && endDate)) {
      // Reset selection if starting fresh
      setStartDate(date);
      setEndDate(null);
    } else if (startDate && !endDate) {
      // Set end date and confirm range
      if (d.isAfter(sd, 'day')) {
        setEndDate(date);
        setDaysCount(d.diff(sd, 'day') + 1);
        setIsDialogOpen(true);
      } else {
        alert('End date must be after the start date');
      }
    }
  };

  // Confirm the selected date
  const handleConfirm = () => {
    if (onConfirm && startDate && endDate) {
      onConfirm(startDate, endDate, { source: 'dialog' });
    }
    setIsDialogOpen(false);
  };

  const onStartDate = () => {
    setStartDate(null);
    setEndDate(null);

    setFlash(true);
    setTimeout(() => setFlash(false), 5);
  }

  const onEndDate = () => {
    setEndDate(null);
  }

  // Render each month in the calendar
  const renderCalendar = () => {
    const monthsArray = [...Array(6).keys()].map(i => today.add(i, 'month'));

    return (
      <Box>
        {monthsArray.map(month => (
          <Box key={month.format('YYYY-MM')} sx={{ marginBottom: 4 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {month.format('MMMM YYYY')}
            </Typography>
	    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', paddingBottom: 1 }}>
	      {renderDaysOfWeekHeaders()}
	    </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {renderDaysInMonth(month)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Render day headers (Sunday to Saturday)
  const renderDaysOfWeekHeaders = () => {
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return daysOfWeek.map(day => (
      <Box key={day} sx={{ textAlign: 'center', fontWeight: 'bold', padding: 1 }}>
        {day}
      </Box>
    ));
  };

  // Render each day in the given month
  const renderDaysInMonth = (month) => {
    const firstDayOfMonth = month.startOf('month').day();
    const daysInMonth = month.daysInMonth();
    const daysArray = [...Array(daysInMonth).keys()].map(i => i + 1);

    const emptyDays = [...Array(firstDayOfMonth).keys()];

    return (
      <>
        {emptyDays.map((_, index) => (
          <Box key={`empty-${index}`} sx={{ height: 64 }} />
        ))}
        {daysArray.map(day => {
          let dateString = month.date(day).format('YYYY-MM-DD');
	  let date = month.date(day).toDate();

          return (
            <DayCard key={dateString} day={day} date={date} onClick={() => handleDayClick(dateString)} />
          );
        })}
      </>
    );
  };

  const getDatePrice = date => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error("Invalid date object");
    }

    const formattedDate = date.toISOString().split('T')[0];
    if (holidaySet.has(formattedDate)) {
      return priceColors.veryHigh;
    }

    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek >= 5) {
      return priceColors.medium; // Weekends
    }
    return priceColors.low; // Weekdays (Mon-Thu)
  }

  // Helper component to render a single day card
  const DayCard = ({ day, date, onClick }) => {
    let priceColor = getDatePrice(date);

    if (flash) {
      priceColor = lightenColor(priceColor, 1.2);

    } else if (isSelected(date) || isInRange(date)) {
      priceColor = getSelectedPriceColor();
      priceColor = lightenColor(priceColor, 1.5);
    }

    //if (isSelected(date)) {
    //  priceColor = lightenColor(priceColor, 1.5);
    //} else if (isInRange(date)) {
    //  priceColor = lightenColor(priceColor, 2);
    //}

    let name = null;
    let formattedDate = date.toISOString().split('T')[0];
    let holiday = holidays.find(h => h.date === formattedDate);
    if (holiday) {
      name = holiday.name;
    }
    const contrastColor = getContrastColor(priceColor, brand);
    const borderColor = contrastColor === '#ffffff' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.12)';

    if (name) {
      let style = {};
      if (isMobile) {
        style.fontSize = '0.5rem';
	style.lineHeight = 1;
      }

      style.color = contrastColor;
      name = <Typography variant="subtitle2" style={style}>{name}</Typography>
    }

    return (
      <Box
        sx={{
          backgroundColor: priceColor,
          color: contrastColor,
          textAlign: 'center',
          padding: 1,
          height: 64,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRadius: 2,
          cursor: 'pointer',
          border: `1px solid ${borderColor}`,
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)'
        }}
        onClick={onClick}
      >
        <Typography variant="h6" sx={{ color: contrastColor }}>{day}</Typography>
	{name}
      </Box>
    );
  };

  return (
    <>
      <CalendarHeader {...{ startDate, endDate, onStartDate, onEndDate }} />
      <Box>{renderCalendar()}</Box>
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Confirm Rental Window</DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f8f5eb' }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{daysCount}</strong> day rental
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Arrival:&nbsp;
            <strong>{startDate ? dayjs(startDate).format('dddd, MMMM D, YYYY') : 'Select a start date'}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Return:&nbsp;
            <strong>{endDate ? dayjs(endDate).format('dddd, MMMM D, YYYY') : 'Select an end date'}</strong>
          </Typography>
          <Chip label="Pricing adjusts for weekends & holidays" color="secondary" />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setIsDialogOpen(false)} color="inherit">
            Keep Browsing
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Calendar;

// Usage Example with Prices Array
// const prices = [
//   { date: '2024-11-01', price: 600 },
//   { date: '2024-11-02', price: 700 },
//   { date: '2024-12-10', price: 800 },
//   { date: '2025-01-04', price: 900 },
//   ...
// ];
// <Calendar prices={prices} isOpen={true} onConfirm={(date) => alert(`Confirmed date: ${date}`)} onClose={() => console.log('Modal closed')} />
